import {Page} from "../page";
import {Card} from "../generic/card";
import {Button, ButtonList, IconButton} from "../generic/button";
import {AsRoute} from "../../app";
import {Column, Columns} from "../generic/columns";
import {FormFooter} from "../generic/form";
import {useEffect, useState} from "preact/hooks";
import {FlashTarget, useFlash} from "../generic/flash";
import {Row, Table} from "../generic/table";
import {mdiClose, mdiPencil, mdiPlus} from "@mdi/js";
import {createRef} from "preact";
import {Badge} from "../generic/badge";
import {DashboardView} from "../dashboardCard";
import {TableRow, TemperatureAPI} from "../../api/temperature";
import {Tab, Tabs} from "../generic/tabs";

export function ComponentDebug(props: AsRoute) {
    const ft = createRef<HTMLDivElement>();
    const {flash} = useFlash();

    const [flashed, setFlashed] = useState<boolean>(false);

    const accents = ["primary", "muted", "danger", "info", "warning", "success"];

    const capitalizeFirstLetter = (text: string) => `${text.substring(0, 1).toUpperCase()}${text.substring(1)}`;

    useEffect(() => {
        if (ft.current && !flashed) {
            flash("Default flash", {target: ft});
            flash("Muted flash", {accent: "muted", target: ft});
            flash("Primary flash", {accent: "primary", target: ft});
            flash("Danger flash", {accent: "danger", target: ft});
            flash("Info flash", {accent: "info", target: ft});
            flash("Warning flash", {accent: "warning", target: ft});
            flash("Success flash", {accent: "success", target: ft});
            setFlashed(true);
        }
    }, [ft]);

    console.log(ft.current);

    const generateTable = (hour: number, reading: number): TableRow => {
        const now = new Date().getTime();
        const from = new Date(now - (hour * 3600 * 1000)).toISOString();
        const to = new Date(now - ((hour + 1) * 3600 * 1000)).toISOString();

        return {
            from, to,
            min: { value: reading, date: from },
            max: { value: reading, date: to },
            average: reading,
            samples: 1
        }
    }

    const fakeAPI: TemperatureAPI = {
        table: async () => new Promise((resolve) => resolve({
            table: [
                generateTable(7, 0),
                generateTable(6, 5.6),
                generateTable(5, 0),
                generateTable(4, -3.4),
                generateTable(3, -5.6),
                generateTable(2, 1.2),
                generateTable(1, 5.6),
            ],
            zDEBUG_execTime: 0.123
        })),
        // @ts-ignore
        sensor: {
            get: async () => new Promise((resolve) => resolve({
                sensors: [
                    {
                        id: 1,
                        name: "Dashboard card",
                        url: "/theme",
                        active: true,
                        last: {
                            value: 12.34,
                            date: new Date().toISOString()
                        },
                        dayMax: {
                            value: 26.12,
                            date: new Date().toISOString()
                        },
                        dayMin: {
                            value: 8.32,
                            date: new Date().toISOString()
                        }
                    },

                    {
                        id: 2,
                        name: "Inactive sensor",
                        url: "/theme",
                        active: false,
                        last: {
                            value: 12.34,
                            date: new Date(new Date().getTime() - 365 * 86400 * 1000).toISOString()
                        },
                        dayMax: null,
                        dayMin: null
                    }
                ],
                zDEBUG_execTime: 0.123
            }))
        }
    }

    return <Page title={"Theme showcase"} showBackButton={true}>
        <Columns>
            <Column>
                <Card
                    fullHeight={false}
                    title="Default card"
                    footer={<FormFooter>
                        <Button>Default</Button>
                        {accents.map(accent => <Button accent={accent}>{capitalizeFirstLetter(accent)}</Button>)}
                    </FormFooter>}
                    style={{padding: "1rem"}}
                >
                    Card content
                </Card>

                {["default", ...accents].map(accent =>
                    <Card fullHeight={false} title={`${capitalizeFirstLetter(accent)} table`} accent={accent}>
                        <Table columns={[
                            {id: "name", title: "Header"},
                            {id: "cell", title: "Other cell"},
                            {id: "actions", title: <ButtonList className="text-right"><IconButton path={mdiPlus} className={"btn"} /></ButtonList>, class: "td-shrink text-right"}
                        ]}>
                            {[1, 2, 3, 4, 5].map(num =>
                                <Row cells={{
                                    "name": <th>{`Row ${num}`}</th>,
                                    "cell": <td>Cell content</td>,
                                    "actions": <td class="td-shrink"><ButtonList>
                                        <IconButton path={mdiPencil} className={"btn"} />
                                        <IconButton path={mdiClose} className={"btn"} />
                                    </ButtonList></td>
                                }} />)}
                        </Table>
                    </Card>
                )}
            </Column>
            <Column>
                {accents.map(accent =>
                    <Card
                        fullHeight={false}
                        title={`${capitalizeFirstLetter(accent)} card`} accent={accent}
                        style={{padding: "1rem"}}
                    >
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean id.
                    </Card>
                )}

                <Columns>
                    <Column>
                        <div style={{padding: "1rem"}}>
                            <FlashTarget ref={ft} />
                        </div>
                    </Column>
                    <Column style={{paddingTop: "1rem"}}>
                        <Card title="Typography" style={{padding: "1rem"}}>
                            <div>{accents.map(accent => <Badge accent={accent}>{capitalizeFirstLetter(accent)}</Badge>)}</div>
                            <div><small>Small text</small></div>
                            <div class="text-danger">Danger text</div>
                            <div class="text-muted">Muted text</div>
                        </Card>
                    </Column>
                </Columns>

                <DashboardView api={fakeAPI} set={{id: 123, url: "/theme", name: "Sensor set"}}/>

                <Tabs>
                    <Tab path="/theme" title="Tab 1">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean id.</Tab>
                    <Tab path="/theme/tab2" title="Tab 2">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean id.</Tab>
                    <Tab path="/theme/tab3" title="Tab 3">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean id.</Tab>
                    <Tab path="/theme/tab4" title="Tab 4">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean id.</Tab>
                    <Tab path="/theme/tab5" title="Tab 5">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Aenean id.</Tab>
                </Tabs>
            </Column>
        </Columns>
    </Page>
}
